import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

const storageKey = 'dar-mercadona:favorites:v1';
/**
 * Device-local product links; no cart or customer data is changed.
 * @extends Component<{dialog: HTMLDialogElement, list: HTMLUListElement, status: HTMLElement, saveButton?: HTMLButtonElement}>
 */
class DarFavorites extends Component {
  requiredRefs = ['dialog', 'list', 'status'];
  #controller;
  #opener;
  #items = [];

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    window.addEventListener('storage', this.#refresh, { signal });
    document.addEventListener('dar:favorites:open', this.#openFromHeader, { signal });
    document.addEventListener('dar:favorites:refresh', this.#refresh, { signal });
    document.addEventListener('dar:favorites:toggle', this.#toggleFromProduct, { signal });
    this.refs.dialog.addEventListener('close', () => {
      unlockScroll(this.refs.dialog);
      if (this.#opener?.isConnected) this.#opener.focus();
    }, { signal });
    for (const button of this.querySelectorAll('[data-launch]')) button.removeAttribute('disabled');
    this.#refresh();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    unlockScroll(this.refs.dialog);
  }

  #refresh = () => {
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) || '[]');
      this.#items = Array.isArray(stored) ? stored.filter((item) => {
        if (!item || typeof item.title !== 'string' || typeof item.url !== 'string') return false;
        try {
          const url = new URL(item.url, location.origin);
          return url.origin === location.origin && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(url.pathname);
        } catch {
          return false;
        }
      }).slice(0, 100) : [];
    } catch {
      this.#items = [];
    }
    this.#render();
  };

  #render() {
    const { list, status, saveButton } = this.refs;
    list.replaceChildren();
    for (const item of this.#items) {
      const row = document.createElement('li');
      const link = document.createElement('a');
      link.href = item.url;
      link.textContent = item.title;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'button-secondary';
      remove.textContent = this.dataset.remove;
      remove.setAttribute('aria-label', `${this.dataset.remove} — ${item.title}`);
      remove.setAttribute('on:click', '/removeFavorite');
      remove.dataset.url = item.url;
      row.append(link, remove);
      list.append(row);
    }
    status.textContent = this.#items.length ? '' : this.dataset.empty;
    for (const button of document.querySelectorAll('[data-dar-product-favorite]')) {
      button.setAttribute('aria-pressed', String(this.#items.some((item) => item.url === button.dataset.url)));
    }
    if (saveButton) {
      const saved = this.#items.some((item) => item.url === saveButton.dataset.url);
      saveButton.setAttribute('aria-pressed', String(saved));
      saveButton.textContent = `${saved ? this.dataset.saved : this.dataset.save} — ${saveButton.dataset.title}`;
    }
  }

  #persist(items) {
    try {
      localStorage.setItem(storageKey, JSON.stringify(items));
      this.#items = items;
      this.#render();
      return true;
    } catch {
      this.refs.status.textContent = this.dataset.error;
      return false;
    }
  }

  /** @param {MouseEvent} event */
  removeFavorite(event) {
    if (!(event.target instanceof HTMLButtonElement)) return;
    const url = event.target.dataset.url;
    const index = this.#items.findIndex((item) => item.url === url);
    this.#persist(this.#items.filter((item) => item.url !== url));
    const buttons = this.refs.list.querySelectorAll('button');
    (buttons[Math.min(index, buttons.length - 1)] || this.refs.dialog.querySelector('button'))?.focus();
  }

  /** Product hearts share this owner's persistence and live feedback.
   * @param {CustomEvent<{trigger: HTMLButtonElement}>} event
   */
  #toggleFromProduct = (event) => {
    const button = event.detail?.trigger;
    if (!(button instanceof HTMLButtonElement) || !button.matches('[data-dar-product-favorite]')) return;
    const url = button.dataset.url;
    const title = button.dataset.title;
    if (!url || !title) return;
    let target;
    try { target = new URL(url, location.origin); } catch { return; }
    if (target.origin !== location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(target.pathname)) return;
    this.#refresh();
    const exists = this.#items.some((item) => item.url === url);
    const saved = this.#persist(exists ? this.#items.filter((item) => item.url !== url) : [...this.#items.slice(-99), { url, title }]);
    const status = button.closest('ozmo-atelier-favorite')?.querySelector('[role="status"]');
    if (status) status.textContent = saved ? (button.getAttribute('aria-pressed') === 'true' ? this.dataset.saved : this.dataset.save) : this.dataset.error;
  };

  /** Header triggers use the same dialog, list and persistence as the mobile navigation.
   * @param {CustomEvent<{trigger: HTMLButtonElement}>} event
   */
  #openFromHeader = (event) => {
    const trigger = event.detail?.trigger;
    if (!(trigger instanceof HTMLButtonElement) || !trigger.isConnected) return;
    this.#showFavorites(trigger);
  };

  /** @param {MouseEvent} event */
  openFavorites(event) {
    this.#showFavorites(event.target instanceof Element ? event.target.closest('button') : undefined);
  }

  /** @param {Element | undefined | null} trigger */
  #showFavorites(trigger) {
    this.#opener = trigger;
    this.#refresh();
    if (this.refs.dialog.open) return;
    this.refs.dialog.showModal();
    lockScroll(this.refs.dialog);
  }

  /** Close via button; Escape is provided by the native dialog. */
  closeFavorites() { this.refs.dialog.close(); }

  /** @param {MouseEvent} event */
  backdropClick(event) {
    if (event.target !== this.refs.dialog) return;
    const rect = this.refs.dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeFavorites();
  }

  /** Save or remove the current Shopify product link. @param {MouseEvent} _event */
  toggleFavorite(_event) {
    this.#refresh();
    const button = this.refs.saveButton;
    if (!button) return;
    const url = button.dataset.url;
    const exists = this.#items.some((item) => item.url === url);
    this.#persist(exists ? this.#items.filter((item) => item.url !== url) : [...this.#items.slice(-99), { url, title: button.dataset.title }]);
  }
}
if (!customElements.get('dar-favorites-component')) customElements.define('dar-favorites-component', DarFavorites);
